import { useEffect, useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'

// Alterações de Salário (TMOTCA3 do ERP, frota.motca3). Um registro por data; excluir não pode.

interface Salario { data: string; vho: string; val: string; vou: string; enc: string; obs: string; dep: number | string }

const fmtNum = (v: any) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtData = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const largura = (max: number) => Math.max(56, max * 9 + 28)
const LINHA: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '0.75rem 0.9rem', alignItems: 'flex-end' }
const VAZIO = { data: '', vho: '', val: '', vou: '', enc: '', obs: '', dep: '0' }

export default function FuncionarioSalarioModal({ motcod, motnome, onClose }: { motcod: string; motnome: string; onClose: () => void }) {
  const { empresaCod } = useEmpresa()
  const [lista, setLista] = useState<Salario[] | null>(null)
  const [f, setF] = useState<Record<string, string>>(VAZIO)
  const [novo, setNovo] = useState(true)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  function selecionar(s: Salario) {
    setF({ data: s.data, vho: fmtNum(s.vho), val: fmtNum(s.val), vou: fmtNum(s.vou), enc: fmtNum(s.enc), obs: s.obs, dep: String(s.dep ?? 0) })
    setNovo(false); setErro(''); setAviso('')
  }

  async function carregar(manterData?: string) {
    const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/salarios`)
    setLista(r.data)
    const alvo = r.data.find((s: Salario) => s.data === manterData) ?? r.data[0]
    if (alvo) selecionar(alvo)
    else { setF(VAZIO); setNovo(true) }
  }

  useEffect(() => {
    carregar().catch(() => setErro('Não foi possível carregar os salários do funcionário.'))
  }, [motcod])

  function novoRegistro() {
    setF(VAZIO); setNovo(true); setErro(''); setAviso('')
  }

  async function salvar() {
    setErro(''); setAviso('')
    if (!f.data) return setErro('Data inválida!')
    setSalvando(true)
    try {
      const r = await api.put(`/funcionarios/${encodeURIComponent(motcod)}/salarios`, { ...f, novo, empresa_logada: empresaCod })
      await carregar(f.data)
      setAviso(r.data?.semAlteracao ? 'Nenhuma alteração para salvar.' : 'Salário salvo.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar o salário.')
    } finally {
      setSalvando(false)
    }
  }

  const set = (c: string, v: string) => setF(p => ({ ...p, [c]: v }))
  const campo = (w: number, label: string, conteudo: React.ReactNode) => (
    <div className="field-cad" style={{ flex: `0 0 ${w}px` }}>
      <label style={{ whiteSpace: 'nowrap' }}>{label}</label>
      {conteudo}
    </div>
  )
  const valor = (c: string, label: string, max: number) =>
    campo(Math.max(largura(max), label.length * 7 + 10), label,
      <input value={f[c]} maxLength={max} inputMode="decimal" style={{ width: '100%', boxSizing: 'border-box', textAlign: 'right' }}
        onChange={e => set(c, e.target.value.replace(/[^\d.,]/g, ''))} />)

  const ultima = lista?.[0]?.data ?? ''
  const th: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.72rem' }
  const td: React.CSSProperties = { padding: '0.35rem 0.6rem', fontSize: '0.82rem' }

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 760, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

        <div className="modal-header" style={{ gap: '1rem' }}>
          <div style={{ minWidth: 0 }}>
            <h2>Alterações de Salário — {motcod} · {motnome}</h2>
            <div style={{ fontSize: '0.75rem', color: 'rgba(219,234,254,0.9)', marginTop: 2 }}>
              Última data: {fmtData(ultima) || '—'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>TMOTCA3</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-corpo">
          {!lista ? (
            <p style={{ color: erro ? '#dc2626' : '#6b7280' }}>{erro || 'Carregando...'}</p>
          ) : (
            <>
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                <table className="table" style={{ width: '100%', margin: 0 }}>
                  <thead>
                    <tr>
                      <th style={th}>Data</th>
                      <th style={{ ...th, textAlign: 'right' }}>Valor Hora</th>
                      <th style={{ ...th, textAlign: 'right' }}>Salário Base</th>
                      <th style={{ ...th, textAlign: 'right' }}>Valor Outros</th>
                      <th style={{ ...th, textAlign: 'right' }}>Encargos %</th>
                      <th style={th}>Observações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.length === 0 && (
                      <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: '#6b7280', padding: '1rem' }}>Nenhum salário lançado.</td></tr>
                    )}
                    {lista.map(s => {
                      const ativo = !novo && s.data === f.data
                      return (
                        <tr key={s.data} onClick={() => selecionar(s)}
                          style={{ cursor: 'pointer', background: ativo ? '#eef3fb' : undefined, fontWeight: ativo ? 600 : undefined }}>
                          <td style={td}>{fmtData(s.data)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{fmtNum(s.vho)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{fmtNum(s.val)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{fmtNum(s.vou)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{fmtNum(s.enc)}</td>
                          <td style={td}>{s.obs || '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '1rem 0 0.6rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {novo ? 'Novo salário' : `Salário de ${fmtData(f.data)}`}
                </span>
                {!novo && <button className="btn btn-secondary btn-sm" onClick={novoRegistro}>+ Novo salário</button>}
              </div>

              <div style={LINHA}>
                {campo(140, 'Data *',
                  <input type="date" value={f.data} disabled={!novo} style={{ width: '100%', boxSizing: 'border-box' }}
                    title={novo ? undefined : 'Data já gravada não pode ser alterada'} onChange={e => set('data', e.target.value)} />)}
                {valor('vho', 'Valor Hora', 10)}
                {valor('val', 'Salário Base', 10)}
                {valor('vou', 'Valor Outros', 10)}
                {valor('enc', 'Encargos Sociais (%)', 5)}
              </div>
              <div style={{ ...LINHA, marginTop: '0.75rem' }}>
                {campo(Math.max(largura(40), 120), 'Observações',
                  <input value={f.obs} maxLength={40} style={{ width: '100%', boxSizing: 'border-box' }}
                    onChange={e => set('obs', e.target.value.toUpperCase())} />)}
                {campo(170, 'Dependentes Plano Saúde',
                  <input type="number" min={0} max={999} value={f.dep} style={{ width: '100%', boxSizing: 'border-box' }}
                    onChange={e => set('dep', e.target.value)} />)}
              </div>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.75rem 0 0' }}>
                O salário mais recente vai para o cadastro (Salário Base, Valor Outros e Encargos).
              </p>
            </>
          )}

          {lista && erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '1rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '1rem 0 0' }}>{aviso}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-orange" onClick={salvar} disabled={salvando || !lista}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
